import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useAttachmentStaging } from '@/hooks/useAttachmentStaging';
import { useEntityComments } from '@/hooks/useEntityComments';
import { RequiredMark, GenericSelect, firstMissingRequiredMessage, type GenericFormField } from '@/styles/GenericForm';
import { GenericAttachmentsSection, GenericAttachment } from '@/styles/GenericAttachmentsSection';
import { GenericCommentsSection, TEAM_CLIENT_VENDOR_AUDIENCES } from '@/styles/GenericCommentsSection';
import { Input } from '@/components/ui/input';
import { DateInput } from '@/components/ui/date-input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { StandardButton, SaveChangesButton, CancelButton, CloseButton } from '@/styles/StandardButtons';
import { VendorField } from '@/components/shared/VendorField';
import '@/design-system/typography.css';
import '@/design-system/forms.css';
import { Bill, Project } from '../types';
import { BillsService } from '../services/billsService';
import type { BillPaymentInput } from '../services/billPaymentsService';
import { useProjectMembershipPrompt } from '@/hooks/useProjectMembershipPrompt';
import { useUser } from '@/contexts/UserContext';
import { useProject, getDefaultProjectIdForNewRecord } from '@/components/ProjectSelector';
import { getApiUrl } from '@/lib/api-config';
import { uploadBillPaymentAttachments, deleteBillAttachment } from '../store';
import { colors } from '@/design-system/colors';

type BillWithBalance = Bill & { amount_paid?: number; vendor?: { company_name?: string | null; first_name?: string | null; last_name?: string | null } | null };

/** The bill a payment is recorded FROM (the Bill form's "Record Payment"). Without `id` the bill is not saved yet: the payment is a draft. */
export interface PaymentBillContext {
  id?: string;
  label: string;
  project_id: string;
  vendor_id: string;
  vendor_name: string;
  total: number;
  amountPaid: number;
  status: string;
}

/** A payment prepared for a bill that does not exist yet: lives in the Bill form's memory only; nothing is written until the bill is saved. */
export interface PaymentDraft {
  vendor_id: string;
  vendor_name: string;
  payment_number: string;
  payment_date: string;
  status: string;
  amount: number;
  notes: string;
  /** Generated once with the draft: the post-save create can be retried with it without ever duplicating the payment. */
  client_request_id: string;
}

const DRAFT_BILL_ID = '__draft__';

interface PaymentFormProps {
  /** Opened from a bill: project and bill are fixed, the balance comes from the bill. */
  billContext?: PaymentBillContext;
  /** A payment already prepared for the unsaved bill (reopening it edits the same draft). */
  draft?: PaymentDraft | null;
  /** Draft mode (bill not saved): receives the finished draft instead of writing anything. */
  onDraft?: (draft: PaymentDraft) => void;
  /** The payment being edited, or null for a new one. */
  payment: any;
  projects: Project[];
  onClose: () => void;
  /** Creates or updates the payment (ONE request) and returns it. The payload carries `id` when it updates. */
  onSave: (payload: Partial<BillPaymentInput> & { id?: string }) => Promise<any>;
  /** The payment (and its files) are saved: refresh the list. Not awaited. */
  onSaved?: () => void;
  isOpen: boolean;
}

const NO_PROJECTS: Project[] = [];
const FORM_INPUT = 'font-form-input generic-form-field';
const todayIso = () => new Date().toISOString().split('T')[0];
const toCents = (n: number | string | null | undefined) => Math.round(Number(n || 0) * 100);
const fromCents = (cents: number) => Math.round(cents) / 100;
const money = (n: number) => `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const normalize = (obj: Record<string, any>): Record<string, string> =>
  Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, v == null ? '' : String(v).trim()]));
const vendorName = (v?: { company_name?: string | null; first_name?: string | null; last_name?: string | null } | null) =>
  v ? v.company_name || `${v.first_name || ''} ${v.last_name || ''}`.trim() : '';

export function PaymentForm({ payment, projects = NO_PROJECTS, onClose, onSave, onSaved, isOpen, billContext, draft, onDraft }: PaymentFormProps) {
  const { companyId, user } = useUser();
  const { ensureMembers, prompt: membershipPrompt } = useProjectMembershipPrompt();
  const { selectedProject } = useProject();
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [bills, setBills] = useState<BillWithBalance[]>([]);
  const [billsLoading, setBillsLoading] = useState(false);
  const [paidInFull, setPaidInFull] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  // A payment created by a save (Save, Export PDF, Send to Vendor): from then on the form EDITS it -- a second click
  // can never create a second payment.
  const [createdPaymentId, setCreatedPaymentId] = useState<string | null>(null);
  const submitLock = useRef(false);
  // One key per logical payment: a double click or a retry after a lost answer is the SAME request to the server.
  const requestKey = useRef<string>('');
  const previousIsOpenRef = useRef(false);
  const initialValuesRef = useRef<Record<string, any>>({});

  const isDraftMode = !!billContext && !billContext.id;
  const activePaymentId: string | undefined = payment?.id || createdPaymentId || undefined;
  const isEdit = !!activePaymentId;

  const { comments, addComment, deleteComment } = useEntityComments('bill_payment', activePaymentId);

  const uploadFn = useCallback(async (id: string, files: File[]): Promise<GenericAttachment[]> => {
    if (!companyId || !user?.id) return [];
    return uploadBillPaymentAttachments(id, files, companyId, user.id) as Promise<GenericAttachment[]>;
  }, [companyId, user?.id]);
  const deleteFn = useCallback(async (attachmentId: string) => (user ? deleteBillAttachment(attachmentId, user.id) : false), [user]);
  const {
    pendingFiles,
    uploadedFiles: uploadedAttachments,
    setUploadedFiles: setUploadedAttachments,
    handleUpload: handleFileUpload,
    handleRemovePending: handleRemovePendingFile,
    handleDelete: handleDeleteAttachment,
    resetPending: resetPendingFiles,
    uploadPending,
  } = useAttachmentStaging<GenericAttachment>({
    entityId: activePaymentId,
    uploadFn,
    deleteFn,
    newFilesPosition: 'prepend',
    onError: setError,
  });

  // Capture the initial values when the form opens (closed -> open), never while it is open.
  useEffect(() => {
    if (isOpen && !previousIsOpenRef.current) {
      const initial = billContext
        ? {
            payment_number: draft?.payment_number || '',
            project_id: billContext.project_id,
            vendor_id: draft?.vendor_id ?? billContext.vendor_id,
            vendor_name: draft ? draft.vendor_name : billContext.vendor_name,
            bill_id: billContext.id || DRAFT_BILL_ID,
            // the suggestion is what is still owed; the user can lower it for a partial payment
            amount: draft?.amount ?? fromCents(Math.max(0, toCents(billContext.total) - toCents(billContext.amountPaid))),
            payment_date: draft?.payment_date || todayIso(),
            status: draft?.status || 'pending',
            notes: draft?.notes || '',
          }
        : payment?.id
        ? {
            payment_number: payment.payment_number || '',
            project_id: payment.project_id || '',
            vendor_id: payment.vendor_id || '',
            vendor_name: vendorName(payment.vendor),
            bill_id: payment.bill_id || '',
            amount: payment.amount || 0,
            payment_date: payment.payment_date || todayIso(),
            status: payment.status || 'pending',
            notes: payment.notes || '',
          }
        : {
            payment_number: '',
            // the global project selector decides the default; "All Projects" leaves it to the user (the field is enabled)
            project_id: getDefaultProjectIdForNewRecord(selectedProject),
            vendor_id: '',
            vendor_name: '',
            bill_id: '',
            amount: 0,
            payment_date: todayIso(),
            status: 'pending',
            notes: '',
          };
      setFormData(initial);
      initialValuesRef.current = normalize(initial);
      setPaidInFull(false);
      setError(null);
      setCreatedPaymentId(null);
      setBills([]);
      submitLock.current = false;
      requestKey.current = draft?.client_request_id || crypto.randomUUID();
      resetPendingFiles();
      setUploadedAttachments(payment?.id && Array.isArray(payment.attachments) ? payment.attachments : []);
    }
    previousIsOpenRef.current = isOpen;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // The bills of the selected project, with what has been paid on each (the server computes it): the Bill dropdown and the
  // amount paid / due. Loaded when the project is known -- not every bill of the company when the form opens.
  const projectId: string = formData.project_id || '';
  useEffect(() => {
    if (!isOpen || !projectId || billContext) {
      setBills([]);
      return;
    }
    let cancelled = false;
    setBillsLoading(true);
    BillsService.getBillsWithBalance(projectId)
      .then((list) => { if (!cancelled) setBills(list as BillWithBalance[]); })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : 'The bills could not be loaded'); })
      .finally(() => { if (!cancelled) setBillsLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen, projectId, billContext]);

  const selectedBill = useMemo<BillWithBalance | null>(() => {
    if (billContext) {
      return { id: billContext.id || DRAFT_BILL_ID, total_amount: billContext.total, amount_paid: billContext.amountPaid, status: billContext.status } as unknown as BillWithBalance;
    }
    return bills.find((b) => b.id === formData.bill_id) || null;
  }, [bills, formData.bill_id, billContext]);

  // What has been paid BEFORE this payment, and what was due before it: when editing, the payment itself is not part of "paid".
  const editedAmountInBill = payment?.id && payment.bill_id === selectedBill?.id && !!payment.id && payment.status !== 'voided' ? toCents(payment.amount) : 0;
  const billTotal = toCents(selectedBill?.total_amount);
  const priorPaid = selectedBill ? Math.max(0, toCents(selectedBill.amount_paid) - editedAmountInBill) : 0;
  const amountDue = selectedBill ? Math.max(0, billTotal - priorPaid) : 0;
  const amountCents = toCents(formData.amount);
  const exceedsDue = !!selectedBill && formData.status !== 'voided' && amountCents > amountDue;

  // Bills that can take a payment: with something still owed -- plus the one this payment already belongs to.
  const billOptions = useMemo(() => billContext ? [{ value: billContext.id || DRAFT_BILL_ID, label: billContext.label }] : bills
    .filter((b) => b.id === payment?.bill_id || (['open', 'partially_paid'].includes(b.status) && toCents(b.total_amount) - toCents(b.amount_paid) > 0))
    .map((b) => ({ value: b.id, label: `${b.bill_id} - ${b.name || 'Untitled Bill'}` })), [bills, payment?.bill_id, billContext]);

  const handleChange = (name: string, value: any) => setFormData((prev) => ({ ...prev, [name]: value }));

  const handleProjectChange = (value: string) => {
    // another project: the chosen bill (and the vendor it brought) no longer applies
    setFormData((prev) => ({ ...prev, project_id: value, bill_id: '', ...(prev.bill_id ? { vendor_id: '', vendor_name: '' } : {}) }));
    setPaidInFull(false);
  };

  const handleBillChange = (billId: string) => {
    const bill = bills.find((b) => b.id === billId);
    setFormData((prev) => ({
      ...prev,
      bill_id: billId,
      // the vendor being paid defaults to the bill's own vendor (only when none was chosen yet)
      ...(bill?.vendor_id && !prev.vendor_id ? { vendor_id: bill.vendor_id, vendor_name: vendorName(bill.vendor) } : {}),
    }));
    setPaidInFull(false);
  };

  // "Paid in full": this payment is exactly what is still due.
  useEffect(() => {
    if (paidInFull && selectedBill) setFormData((prev) => ({ ...prev, amount: fromCents(amountDue) }));
  }, [paidInFull, selectedBill, amountDue]);

  const isDirty = () => JSON.stringify(normalize(formData)) !== JSON.stringify(initialValuesRef.current) || pendingFiles.length > 0;

  const dirtyNow = isOpen && isDirty();
  useEffect(() => {
    if (!dirtyNow) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirtyNow]);

  const handleCloseAttempt = () => {
    if (!isDirty() || window.confirm('Are you sure you want to lose all unsaved progress?')) onClose();
  };

  const requiredFields: GenericFormField[] = [
    { name: 'project_id', label: 'Project', type: 'select', required: true },
  ];

  /** Saves the payment; resolves with it (or undefined when the form refused). `closeAfter` false for Export / Send. */
  const savePayment = async (closeAfter: boolean): Promise<any | undefined> => {
    if (submitLock.current) return undefined; // a ref, not state: a second click in the same tick finds the lock closed
    const missing = firstMissingRequiredMessage(requiredFields, formData);
    if (missing) {
      setError(missing);
      return undefined;
    }
    if (amountCents <= 0) {
      setError('The payment amount must be greater than zero');
      return undefined;
    }
    if (exceedsDue) {
      setError(`This payment (${money(fromCents(amountCents))}) is more than the amount due (${money(fromCents(amountDue))})`);
      return undefined;
    }
    if (!isDraftMode && !(await ensureMembers(formData.project_id, [{ id: formData.vendor_id || '', name: formData.vendor_name || 'The vendor' }]))) return undefined;
    if (isDraftMode) {
      // nothing is written: the draft goes back to the Bill form, which records it right after the bill is saved
      onDraft?.({
        vendor_id: formData.vendor_id || '',
        vendor_name: formData.vendor_name || '',
        payment_number: formData.payment_number?.trim() || '',
        payment_date: formData.payment_date,
        status: formData.status || 'pending',
        amount: fromCents(amountCents),
        notes: formData.notes || '',
        client_request_id: requestKey.current,
      });
      onClose();
      return undefined;
    }
    submitLock.current = true;
    setIsSubmitting(true);
    setError(null);
    try {
      const payload: Partial<BillPaymentInput> & { id?: string } = {
        project_id: formData.project_id,
        // optional: a payment may be recorded without a bill (cleared on an existing payment sends null)
        bill_id: formData.bill_id || null,
        vendor_id: formData.vendor_id || null,
        payment_number: formData.payment_number?.trim() || null,
        payment_date: formData.payment_date,
        status: formData.status || 'pending',
        amount: fromCents(amountCents),
        notes: formData.notes || null,
      };
      const saved = await onSave(isEdit ? { id: activePaymentId, ...payload } : { ...payload, client_request_id: requestKey.current });
      if (!saved) throw new Error('The payment could not be saved');
      if (!isEdit) setCreatedPaymentId(saved.id);

      await uploadPending(saved.id);
      initialValuesRef.current = normalize(formData); // what is on the server now: not "unsaved" any more
      onSaved?.();
      if (closeAfter) onClose();
      return saved;
    } catch (err: any) {
      console.error('Error saving payment:', err);
      // the form keeps everything that was typed; the same key makes a retry safe
      setError(err?.message || 'Failed to save payment');
      return undefined;
    } finally {
      submitLock.current = false;
      setIsSubmitting(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void savePayment(true);
  };

  /** Export PDF: the payment's own printable page (opens the browser's print dialog -> Save as PDF). A new payment is saved first. */
  const handleExportPDF = async () => {
    const saved = activePaymentId ? { id: activePaymentId } : await savePayment(false);
    if (!saved?.id) return;
    // the authenticated preview page of the document prints itself (?print=1)
    window.open(`/documents/bill_payment/${saved.id}/preview?print=1`, '_blank', 'noopener,noreferrer');
  };

  const handleSendToVendor = async () => {
    const saved = activePaymentId ? { id: activePaymentId } : await savePayment(false);
    if (!saved?.id || !companyId) return;
    setIsSendingEmail(true);
    setError(null);
    try {
      // The recipient (the vendor) is resolved server-side from the payment; the status becomes 'posted' there after a successful send.
      const response = await fetch(getApiUrl(`api/documents/bill_payment/${saved.id}/share`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.error || result.message || 'Failed to send payment');
      if (!result.success) throw new Error(result.error || 'Failed to send payment');
      if (!result.emailed) throw new Error('No vendor email found for this payment');
      onSaved?.();
      onClose();
    } catch (err: any) {
      console.error('Error sending payment:', err);
      setError(err.message || 'Failed to send payment to vendor');
    } finally {
      setIsSendingEmail(false);
    }
  };

  if (!isOpen) return null;

  const mode = isEdit ? 'edit' : 'create';
  const busy = isSubmitting || isSendingEmail;

  const layout = (
    <div className="generic-form-overlay" style={{ top: 0, left: 0, right: 0, bottom: 0 }} onClick={(e) => e.target === e.currentTarget && handleCloseAttempt()}>
      <form
        onSubmit={handleSubmit}
        className="generic-form-container"
        style={{ width: 'var(--form-width-default)', maxHeight: 'var(--form-max-height-default)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="generic-form-header generic-form-header-default" style={{ backgroundColor: colors.formHeaderFooter }}>
          <h2 className="font-form-title">{payment?.id ? 'Edit Payment' : billContext ? 'Record Payment' : 'Create Payment'}</h2>
          <CloseButton onClick={handleCloseAttempt} />
        </div>

        <div className="generic-form-body generic-form-body-default">
          {error && <div className="generic-form-error whitespace-pre-line">{error}</div>}

          <div className="space-y-6">
            {payment?.payment_id && (
              <div className="space-y-2">
                <Label htmlFor="pay_payment_id" className="font-form-label">Payment ID</Label>
                <Input id="pay_payment_id" value={payment.payment_id} readOnly className="font-form-input generic-form-field bg-gray-100 cursor-not-allowed" />
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="pay_payment_number" className="font-form-label">Payment Number</Label>
                <Input id="pay_payment_number" value={formData.payment_number || ''} onChange={(e) => handleChange('payment_number', e.target.value)} placeholder="Enter payment number" className={FORM_INPUT} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pay_project_id" className="font-form-label">Project<RequiredMark required /></Label>
                <GenericSelect
                  id="pay_project_id"
                  value={projectId}
                  onValueChange={handleProjectChange}
                  disabled={!!billContext}
                  placeholder="Select"
                  required
                  contentClassName="max-h-[18.75rem] overflow-y-auto"
                  options={projects.map((p) => ({ label: p.name, value: p.id }))}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="pay_bill_id" className="font-form-label">Bill</Label>
                <GenericSelect
                  id="pay_bill_id"
                  value={formData.bill_id || ''}
                  onValueChange={handleBillChange}
                  placeholder={!projectId ? 'Select a project first' : billsLoading ? 'Loading bills...' : billOptions.length === 0 ? 'No bills with a balance' : 'None (optional)'}
                  disabled={!!billContext || !projectId || billsLoading}
                  contentClassName="max-h-[18.75rem] overflow-y-auto"
                  options={billOptions}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pay_vendor_name" className="font-form-label">Vendor</Label>
                <VendorField
                  id="pay_vendor_name"
                  value={formData.vendor_name || ''}
                  allowFreeText={false}
                  placeholder="Select vendor"
                  onChange={({ id, name }) => setFormData((prev) => ({ ...prev, vendor_id: id || '', vendor_name: name }))}
                />
              </div>
            </div>

            {/* The payment and the money around it: this payment, what was paid before, what is due */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <Label htmlFor="pay_amount" className="font-form-label">Payment amount ($)</Label>
                <Input
                  id="pay_amount"
                  type="number"
                  step="0.01"
                  value={formData.amount || ''}
                  onChange={(e) => handleChange('amount', fromCents(toCents(e.target.value)))}
                  placeholder="$0.00"
                  aria-invalid={exceedsDue}
                  className={`${FORM_INPUT} [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                  onKeyDown={(e) => { if (['ArrowUp', 'ArrowDown'].includes(e.key)) e.preventDefault(); }}
                  onWheel={(e) => { e.preventDefault(); e.stopPropagation(); }}
                />
                {/* "Paid in full" fills in what is still due. Recording a payment FROM a bill already starts at that amount, so there the box would do
                    nothing: it is only offered where a bill is picked afterwards (Payments) */}
                {!billContext && (
                  <div className="flex items-center gap-2">
                    <input id="paidInFull" type="checkbox" checked={paidInFull} disabled={!selectedBill} onChange={(e) => setPaidInFull(e.target.checked)} className="h-4 w-4" />
                    <Label htmlFor="paidInFull" className="text-sm font-normal cursor-pointer">Paid in full</Label>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="amount_paid" className="font-form-label">Amount paid ($)</Label>
                <Input id="amount_paid" value={selectedBill ? money(fromCents(priorPaid)) : '—'} readOnly disabled className={FORM_INPUT} />
                <p className="text-xs text-gray-500">Paid before this payment</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="amount_due" className="font-form-label">Amount due ($)</Label>
                <Input id="amount_due" value={selectedBill ? money(fromCents(amountDue)) : '—'} readOnly disabled className={FORM_INPUT} />
                <p className={`text-xs ${exceedsDue ? 'text-destructive' : 'text-gray-500'}`}>
                  {exceedsDue ? 'This payment is more than what is due' : selectedBill ? `Bill total ${money(fromCents(billTotal))}` : 'Not linked to a bill'}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="pay_payment_date" className="font-form-label">Payment Date</Label>
                <DateInput id="pay_payment_date" value={formData.payment_date || ''} onChange={(iso) => handleChange('payment_date', iso)} className={FORM_INPUT} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pay_status" className="font-form-label">Status</Label>
                <GenericSelect
                  id="pay_status"
                  value={formData.status || 'pending'}
                  onValueChange={(value) => handleChange('status', value)}
                  placeholder="Select"
                  required
                  options={[{ label: 'Pending', value: 'pending' }, { label: 'Posted', value: 'posted' }, { label: 'Voided', value: 'voided' }]}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="pay_notes" className="font-form-label">Notes</Label>
              <Textarea id="pay_notes" value={formData.notes || ''} onChange={(e) => handleChange('notes', e.target.value)} placeholder="Enter notes" />
            </div>

            {!isDraftMode && (
              <>
            <GenericAttachmentsSection
              attachments={uploadedAttachments}
              pendingFiles={pendingFiles}
              onUpload={handleFileUpload}
              onDelete={isEdit ? handleDeleteAttachment : undefined}
              onRemovePending={handleRemovePendingFile}
              projectId={formData.project_id || ''} mode={mode}
              disabled={false}
            />

            <GenericCommentsSection
              comments={comments}
              onAddComment={async (content, audience) => { await addComment(content, audience); }}
              onDeleteComment={async (commentId) => {
                if (!window.confirm('Are you sure you want to delete this comment?')) return;
                await deleteComment(commentId);
              }}
              mode={mode}
              audiences={TEAM_CLIENT_VENDOR_AUDIENCES}
              placeholder={isEdit ? 'Add a comment...' : 'Comments are available after creating the payment'}
            />
              </>
            )}
          </div>
        </div>

        <div className="generic-form-footer generic-form-footer-default" style={{ backgroundColor: colors.formHeaderFooter }}>
          <CancelButton onClick={handleCloseAttempt}>Cancel</CancelButton>
          {!isDraftMode && (
            <>
              <StandardButton type="button" onClick={handleSendToVendor} customVariant="white" disabled={busy}>
                {isSendingEmail ? 'Sending...' : 'Send to Vendor'}
              </StandardButton>
              <StandardButton type="button" onClick={handleExportPDF} customVariant="white" disabled={busy}>
                Export PDF
              </StandardButton>
            </>
          )}
          <SaveChangesButton disabled={busy}>{isSubmitting ? 'Saving...' : isDraftMode ? 'Add Payment' : billContext ? 'Save Payment' : 'Save Changes'}</SaveChangesButton>
        </div>
      </form>
    </div>
  );

  return (
    <>
      {membershipPrompt}
      {createPortal(layout, document.body)}
    </>
  );
}
